import React, { createContext, useContext } from 'react';
import { KERNEL_LOCALES } from './kernelLocales';

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Unione ricorsiva: i valori di `override` (dizionario del modulo) prevalgono su `base` (kernel).
const mergeDeep = (base = {}, override = {}) => {
  const out = { ...base };
  Object.keys(override).forEach((k) => {
    out[k] = isObject(out[k]) && isObject(override[k]) ? mergeDeep(out[k], override[k]) : override[k];
  });
  return out;
};

/**
 * Motore i18n condiviso: dizionari piatti per lingua + risolutore a
 * dot-path. Ogni modulo costruisce il proprio dizionario LOCALES
 * (merge dei propri file JSON di namespace) e lo passa qui — l'unica
 * parte davvero di modulo è quell'elenco di import, non la logica sotto.
 *
 * Uso in ogni app (frontend/src/i18n/LanguageContext.js):
 *
 *   const LOCALES = { 'it-IT': { ...itCommon, ...itAuth }, 'en-GB': {...} };
 *   export const SUPPORTED_LOCALES = ['it-IT', 'en-GB'];
 *   export const { isValidLocale, getLang, resolve, translate, LanguageProvider, useLanguage } =
 *     createLanguageEngine(LOCALES, SUPPORTED_LOCALES);
 */
export function createLanguageEngine(moduleLocales, supportedLocales) {
  const defaultLocale = supportedLocales[0];
  const locales = {};
  supportedLocales.forEach((lang) => {
    locales[lang] = mergeDeep(KERNEL_LOCALES[lang], moduleLocales[lang]);
  });

  const isValidLocale = (lang) => supportedLocales.includes(lang);

  let currentLang = defaultLocale;

  const getLang = () => currentLang;

  const resolve = (key, lang) => {
    const dict = locales[lang] || locales[defaultLocale];
    return key.split('.').reduce((obj, k) => (obj ? obj[k] : undefined), dict);
  };

  const translate = (key, lang, fallback) => {
    const value = resolve(key, lang || currentLang);
    if (value !== undefined) return value;
    return fallback !== undefined ? fallback : key;
  };

  const LanguageContext = createContext(null);

  function LanguageProvider({ lang = defaultLocale, setLang, children }) {
    currentLang = lang;

    const t = (key, params) => {
      let str = translate(key, lang);
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          str = str.split(`{${k}}`).join(v);
        });
      }
      return str;
    };

    const value = { lang, setLang, t };

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
  }

  function useLanguage() {
    return useContext(LanguageContext);
  }

  return { isValidLocale, getLang, resolve, translate, LanguageProvider, useLanguage };
}
